"use client";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { get_detail } from "@/services/search-service";
import { MonitorIcon, TwitterIcon } from "lucide-react";

const fmtNum = (n: number | null | undefined) =>
    n == null ? "—" : n.toLocaleString();

export default function JobDetail() {
    const { jobId } = useParams();
    const navigate = useNavigate();

    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!jobId) return;

        const fetchDetail = async () => {
            setLoading(true);
            try {
                const res = await get_detail(jobId);
                setData(res);
            } catch (err) {
                console.log(err);
            } finally {
                setLoading(false);
            }
        };

        fetchDetail();
    }, [jobId]);

    if (loading)
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-sm font-mono text-muted-foreground">
                    Loading...
                </div>
            </div>
        );

    if (!data)
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-sm font-mono text-muted-foreground">
                    No data
                </div>
            </div>
        );

    return (
        <div className="min-h-screen bg-background text-foreground">
            <div className="max-w-6xl mx-auto p-6">
                <button
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-2 rounded-md border border-primary/20 px-4 py-2 text-sm font-mono hover:bg-primary/10 transition"
                >
                    ← Back
                </button>

                <div className="mt-6 space-y-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <h1 className="text-2xl font-mono">Job Detail</h1>
                        <div className="text-xs font-mono text-muted-foreground">
                            Job ID: <span className="text-foreground">{data.job_id}</span>
                        </div>
                    </div>

                    {/* X */}
                    <section className="space-y-3">
                        <h2 className="text-lg font-mono flex flex-row gap-2 items-center">
                            <TwitterIcon />
                            <span>X (Twitter)</span>
                        </h2>

                        {data.platforms.x.map((group: any[], idx: number) => (
                            <div key={idx} className="border border-primary/20 bg-background/40 rounded-lg p-4">
                                {group.map((post: any) => (
                                    <div
                                        key={post.id}
                                        className="border-b border-primary/10 pb-3 mb-3 last:border-b-0 last:mb-0 font-mono"
                                    >
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="font-bold">{post.name}</div>
                                            <div className="text-xs font-mono text-muted-foreground">
                                                @{post.username}
                                            </div>
                                        </div>

                                        <div className="mt-2 text-sm leading-relaxed">
                                            {post.text}
                                        </div>

                                        {/* hashtags */}
                                        {post.hashtags?.length > 0 && (
                                            <div className="mt-2 flex flex-wrap gap-2">
                                                {post.hashtags.map((h: string, i: number) => (
                                                    <span
                                                        key={i}
                                                        className="text-xs font-mono px-2 py-1 border border-primary/20 rounded-md"
                                                    >
                                                        #{h}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        <div className="mt-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
                                            <span className="px-2 py-1 border border-primary/20 rounded">
                                                ❤️ {post.metrics.likes}
                                            </span>
                                            <span className="px-2 py-1 border border-primary/20 rounded">
                                                💬 {post.metrics.replies}
                                            </span>
                                            <span className="px-2 py-1 border border-primary/20 rounded">
                                                🔁 {post.metrics.retweets}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </section>

                    {/* YouTube */}
                    <section className="space-y-3">
                        <h2 className="text-lg font-mono flex flex-row gap-2 items-center">
                            <MonitorIcon />
                            <span>YouTube</span>
                        </h2>

                        {data.platforms.youtube.map((yt: any, idx: number) => (
                            <div key={idx} className="border border-primary/20 bg-background/40 rounded-lg p-4">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="font-mono text-sm text-foreground">
                                        Query: {yt.query}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        {yt.results_count} results •{" "}
                                        {new Date(yt.searched_at).toLocaleString()}
                                    </div>
                                </div>

                                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {yt.videos.map((v: any) => (
                                        <div key={v.id} className="border border-primary/20 rounded-lg overflow-hidden bg-background/60">
                                            <img
                                                src={v.thumbnail}
                                                className="w-full h-28 object-cover"
                                            />

                                            <div className="p-3">
                                                <a
                                                    href={v.url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="font-bold block hover:underline"
                                                >
                                                    {v.title}
                                                </a>

                                                <div className="mt-1 text-xs text-muted-foreground">
                                                    {v.uploader} • {fmtNum(v.view_count)} views
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </section>
                </div>
            </div>
        </div>
    );
}
